import { createContext, useContext, useMemo, type ReactNode } from "react";

import type {
  FilterDefinitionType,
  FilterType,
  SortableDefinitionsType,
  SortableType,
} from "@/components/@types";
import { State } from "@/state";

type FilterChipStateContextValue = {
  filters: FilterType[];
  filterDefinitions: Record<string, FilterDefinitionType>;
  setFilters: (filters: FilterType[]) => void;
  sortables?: SortableType[];
  sortableDefinitions?: Record<string, SortableDefinitionsType>;
  setSortables?: (sortables: SortableType[]) => void;
};

const FilterChipStateContext =
  createContext<FilterChipStateContextValue | null>(null);

/**
 * A utility bar's own chips, for a bar that is not the open list's: its
 * filters and, when given, its sort. Without it the chips are the list's, in
 * the store.
 */
export function FilterChipStateProvider({
  children,
  filters,
  filterDefinitions,
  onFiltersChange,
  sortables,
  sortableDefinitions,
  onSortablesChange,
}: {
  children: ReactNode;
  filters: FilterType[];
  filterDefinitions: Record<string, FilterDefinitionType>;
  onFiltersChange: (filters: FilterType[]) => void;
  sortables?: SortableType[];
  sortableDefinitions?: Record<string, SortableDefinitionsType>;
  onSortablesChange?: (sortables: SortableType[]) => void;
}) {
  const value = useMemo(
    () => ({
      filters,
      filterDefinitions,
      setFilters: onFiltersChange,
      sortables,
      sortableDefinitions,
      setSortables: onSortablesChange,
    }),
    [
      filterDefinitions,
      filters,
      onFiltersChange,
      sortables,
      sortableDefinitions,
      onSortablesChange,
    ],
  );

  return (
    <FilterChipStateContext.Provider value={value}>
      {children}
    </FilterChipStateContext.Provider>
  );
}

export function useFilterChipState() {
  const context = useContext(FilterChipStateContext);
  const globalFilters = State((state) => state.filters);
  const globalFilterDefinitions = State((state) => state.filterDefinitions);

  return {
    filters: context?.filters ?? globalFilters,
    filterDefinitions:
      context?.filterDefinitions ?? globalFilterDefinitions ?? {},
    setFilters:
      context?.setFilters ??
      ((filters: FilterType[]) => {
        State.setState({ filters });
      }),
    isControlled: context != null,
  };
}

/** The sort chip's: the bar's own sort when it was given one, else the list's. */
export function useSortChipState() {
  const context = useContext(FilterChipStateContext);
  const globalSortables = State((state) => state.sortables);
  const globalSortableDefinitions = State((state) => state.sortableDefinitions);

  if (context?.setSortables) {
    return {
      sortables: context.sortables ?? [],
      sortableDefinitions: context.sortableDefinitions ?? {},
      setSortables: context.setSortables,
    };
  }
  return {
    sortables: globalSortables,
    sortableDefinitions: globalSortableDefinitions,
    setSortables: (sortables: SortableType[]) => {
      State.setState({ sortables });
    },
  };
}

export function useFilterChipMutations() {
  const { filters, setFilters } = useFilterChipState();

  return {
    filters,
    setFilters,
    updateFilterAtIndex: (
      filterIndex: number,
      updater: (filter: FilterType) => FilterType,
    ) => {
      setFilters(
        filters.map((filter, index) =>
          index === filterIndex ? updater(filter) : filter,
        ),
      );
    },
    removeFilterAtIndex: (filterIndex: number) => {
      setFilters(filters.filter((_, index) => index !== filterIndex));
    },
    appendFilter: (filter: FilterType) => {
      setFilters([...filters, filter]);
    },
  };
}
